import React from 'react'
import { CSVLink } from "react-csv";
import { normalizePhone } from '../../utils/functions/tools/normalizePhone';
import {
  DataTable,
  Table,
  Link,
  Button,
  Pagination,
  TableBatchAction,
  TableBatchActions,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableSelectAll,
  TableSelectRow,
  TableToolbar,
  TableToolbarAction,
  TableToolbarContent,
  TableToolbarSearch,
  TableToolbarMenu,
  Tag,
  Modal,
  NotificationActionButton,
  InlineNotification,
  OverflowMenu,
  OverflowMenuItem,
  Form,
  TextInput,
  DatePicker,
  DatePickerInput
} from 'carbon-components-react';
import {
  Delete16 as Delete,
  Save16 as Save,
  Download16 as Download,
  Edit16 as Edit
} from '@carbon/icons-react';

const clientListHeaders = [
  { header: "ID", key: "id" },
  { header: "Name", key: "name" },
  { header: "Start Date", key: "startdate" },
  { header: "End Date", key: "enddate" },
  { header: "Action", key: "action" }
];

export default {
  title: 'Components/DataTable/Batch Actions',
  component: DataTable,
  subcomponents: {
    TableBatchAction,
    TableBatchActions,
    TableToolbar,
    TableToolbarAction,
    TableToolbarContent,
    TableToolbarSearch,
    TableToolbarMenu,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
  },
};

export const AgreementsList = ({ curDate, dup, setDup, dupForm, duplicateAgreement, openDupModal, setOpenDupModal, clientListReport, setClientsToDel, clientsToDel, error, setError, setOpenDeleteModal, openDeleteModal, batchDelete, rows, totalItems, currentPageSize, setCurrentPageSize, setFirstRowIndex, setClientSearch }: any) => (
  <>
    <DataTable rows={rows} headers={clientListHeaders} isSortable>
      {({
        rows,
        headers,
        getHeaderProps,
        getRowProps,
        getSelectionProps,
        getToolbarProps,
        getBatchActionProps,
        onInputChange,
        selectedRows,
        getTableProps,
        getTableContainerProps,
      }) => {
        const batchActionProps = getBatchActionProps();
        return (
          <TableContainer
            title="Agreement List"
            description="List of all agreements in the system."
            {...getTableContainerProps()}>
            <TableToolbar {...getToolbarProps()}>
              <TableBatchActions {...batchActionProps}>
                <TableBatchAction
                  tabIndex={batchActionProps.shouldShowBatchActions ? 0 : -1}
                  renderIcon={Delete}
                  onClick={() => setOpenDeleteModal(true) & setClientsToDel(selectedRows)}
                >
                  Delete
                </TableBatchAction>
              </TableBatchActions>
              <TableToolbarContent
                aria-hidden={batchActionProps.shouldShowBatchActions}>
                <TableToolbarSearch
                  persistent={true}
                  tabIndex={batchActionProps.shouldShowBatchActions ? -1 : 0}
                  onChange={(e) => setClientSearch(e.target.value)}
                  placeholder="Search clients"
                />
                <TableToolbarMenu
                  tabIndex={batchActionProps.shouldShowBatchActions ? -1 : 0}>
                  <TableToolbarAction>
                    <CSVLink className="text-dark dec-none" {...clientListReport}>Export to CSV</CSVLink>
                  </TableToolbarAction>
                </TableToolbarMenu>
                <Button
                  tabIndex={batchActionProps.shouldShowBatchActions ? -1 : 0}
                  size="small"
                  kind="primary"
                  onClick={(e) => {
                    e.preventDefault();
                    window.location.href = '/agreements/add';
                  }}>
                  Add new
                </Button>
              </TableToolbarContent>
            </TableToolbar>
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  <TableSelectAll {...getSelectionProps()} />
                  {headers.map((header, i) => (
                    <TableHeader key={i} {...getHeaderProps({ header })}>
                      {header.header}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, i) => (
                  <TableRow key={i} {...getRowProps({ row })}>
                    <TableSelectRow {...getSelectionProps({ row })} />
                    {row.cells.map((cell) => (
                      <TableCell key={cell.id}>
                        {cell.info.header === "name" ? <Link href={`/agreements/${row.id}`}>{cell.value}</Link>
                          : cell.info.header === "action" ? <OverflowMenu renderIcon={Edit} className="m-auto">
                            <OverflowMenuItem onClick={() => setDup(row.id) & setOpenDupModal(true)} itemText="Duplicate" />
                          </OverflowMenu> : cell.value}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination
              totalItems={totalItems}
              pageSizes={[10, 20, 30, 40, 50]}
              pageSize={currentPageSize}
              onChange={({ page, pageSize }) => {
                if (pageSize !== currentPageSize) {
                  setCurrentPageSize(pageSize);
                }
                setFirstRowIndex(pageSize * (page - 1));
              }}
            />
          </TableContainer>
        );
      }}
    </DataTable>

    <Modal
      open={openDupModal}
      modalHeading="Duplicate Agreement"
      modalLabel="Agreement List"
      primaryButtonText="Create"
      secondaryButtonText="Cancel"
      hasForm={true}
      onRequestClose={() => setOpenDupModal(false) & setError(false)}
      onRequestSubmit={() => dupForm.current && dupForm.current.click() & setError(false)}
    >   {error && <InlineNotification kind="error" hideCloseButton={true} title="Error: Unable to duplicate agreement." subtitle="Please try again or contact your administrator." actions={<NotificationActionButton onClick={(() => setError(false))}>Dismiss</NotificationActionButton>} />}
      <p style={{ marginBottom: '1rem' }}>
        Fill out the form to duplicate the selected agreement.
      </p>
      <Form id="dupForm" onSubmit={(values) => duplicateAgreement(values)} className="d-flex flex-w">
        <div className="col-12 col-md-6 m-005">
          <TextInput
            data-modal-primary-focus
            id="name"
            name="name"
            labelText="Agreement Name"
            placeholder="Enter agreement name"
            invalidText="Agreement Name is required"
            required
          />
        </div>
        <div className="col-12 col-md-6 m-005">
          <DatePicker datePickerType="single" dateFormat="Y-m-d" style={{ marginBottom: '1rem' }}>
            <DatePickerInput
              required
              id="startdate"
              name="startdate"
              placeholder="yyyy-mm-dd"
              pattern="[0-9]{4}-(0[1-9]|1[012])-(0[1-9]|1[0-9]|2[0-9]|3[01])"
              labelText="Start date"
              defaultValue={curDate}
            />
          </DatePicker>
        </div>
        <div className="col-12 col-md-6 m-005">
          <DatePicker datePickerType="single" dateFormat="Y-m-d" style={{ marginBottom: '1rem' }}>
            <DatePickerInput
              id="enddate"
              name="enddate"
              placeholder="yyyy-mm-dd"
              pattern="[0-9]{4}-(0[1-9]|1[012])-(0[1-9]|1[0-9]|2[0-9]|3[01])"
              labelText="End date"
            />
          </DatePicker>
        </div>
        <input type="submit" style={{ display: 'none' }} ref={dupForm} />
      </Form>
    </Modal>

    <Modal
      danger
      open={openDeleteModal}
      modalHeading="Delete Agreement(s)"
      modalLabel="Agreement List"
      primaryButtonText="Delete Agreement(s)"
      secondaryButtonText="Cancel"
      onRequestClose={() => setOpenDeleteModal(false) & setError(false)}
      onRequestSubmit={() => batchDelete(clientsToDel)}
    >
      {error && <InlineNotification kind="error" hideCloseButton={true} title="Error: Unable to delete agreement(s)" subtitle="Please try again or contact your administrator." actions={<NotificationActionButton onClick={(() => setError(false))}>Dismiss</NotificationActionButton>} />}
      <p>Are you sure you want to delete the selected agreement(s)?</p>
    </Modal>
  </>
);